"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const react_native_paper_1 = require("react-native-paper");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const react_2 = require("@floating-ui/react");
const react_i18next_1 = require("react-i18next");
const Theme_1 = require("../Theme");
const ThemeContext_1 = require("../ThemeContext");
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
const SearchableInput_shared_1 = require("./SearchableInput.shared");
const Helper_1 = require("../Helper");
const CAPSULE_HEIGHT = 36;
const ICON_HEIGHT = 36;
const useStyles = (isFocused) => {
    const THEME = (0, ThemeContext_1.useTheme)();
    return react_native_1.StyleSheet.create({
        headerContent: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            height: CAPSULE_HEIGHT,
        },
        searchCapsule: {
            position: 'static',
            flexDirection: 'row',
            alignItems: 'center',
            height: CAPSULE_HEIGHT,
            borderRadius: CAPSULE_HEIGHT / 2,
            overflow: 'hidden',
            backgroundColor: '#ffffff22',
            borderWidth: isFocused ? 2 : 1,
            borderColor: isFocused ? THEME.AlwaysWhite : `${THEME.Outline}33`,
            boxSizing: 'border-box',
        },
        leftOverlay: {
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 2,
            justifyContent: 'center',
            alignItems: 'center',
        },
        iconTouchArea: {
            width: ICON_HEIGHT,
            height: ICON_HEIGHT,
            justifyContent: 'center',
            alignItems: 'center',
        },
        inputWrapper: {
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'row',
        },
        paperInput: {
            flex: 1,
            backgroundColor: 'transparent',
            fontSize: 14,
            color: THEME.AlwaysWhite,
            borderWidth: 0,
        },
        rightOverlay: {
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
        },
        closeCircleButton: {
            width: 22,
            height: 22,
            borderRadius: 11,
            backgroundColor: THEME.SmokeWhite,
            justifyContent: 'center',
            alignItems: 'center',
        },
        iconButtonSM: {
            width: 36,
            height: 36,
            borderRadius: 18,
            borderWidth: 1,
            borderColor: `${THEME.Outline}33`,
            backgroundColor: '#ffffff22',
            justifyContent: 'center',
            alignItems: 'center',
        },
    });
};
const ExpandableSearchBar = react_1.default.forwardRef(({ maxWidth = 360, placeholder = '', value: controlledValue, onChangeText, onSearch, disableAnimation = false, closeSidebar = false, ...restProps }, ref) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const styles = useStyles(disableAnimation);
    const searchableContext = (0, SearchableInput_shared_1.useOptionalSearchable)();
    const [localValue, setLocalValue] = react_1.default.useState('');
    const [localIsOpen, setLocalIsOpen] = react_1.default.useState(false);
    // Context / State Fallbacks
    const searchText = searchableContext ? searchableContext.searchText : (controlledValue ?? localValue);
    const setSearchText = searchableContext ? searchableContext.setSearchText : (onChangeText || setLocalValue);
    const isOpen = searchableContext ? searchableContext.isOpen : localIsOpen;
    const setIsOpen = searchableContext ? searchableContext.setIsOpen : setLocalIsOpen;
    const reason = searchableContext ? searchableContext.reason : null;
    const setReason = searchableContext ? searchableContext.setReason : null;
    const [isExpanded, setIsExpanded] = react_1.default.useState(disableAnimation);
    const inputRef = react_1.default.useRef(null);
    const capsuleRef = searchableContext ? searchableContext.capsuleRef : react_1.default.useRef(null);
    const leftBtnRef = searchableContext ? searchableContext.leftButtonRef : react_1.default.useRef(null);
    const rightBtnRef = searchableContext ? searchableContext.rightButtonRef : react_1.default.useRef(null);
    const isDesktopWeb = Helper_1.Helper.isPlatformWeb && size !== 'sm';
    const finalPlaceholder = placeholder || t('common:text.search');
    const animProgress = react_1.default.useRef(new react_native_1.Animated.Value(disableAnimation ? 1 : 0)).current;
    react_1.default.useEffect(() => {
        if (disableAnimation) {
            if (!isOpen) {
                if (reason !== 'user-initiated') {
                    setSearchText('');
                }
            }
            setIsExpanded(true);
            return;
        }
        if (isDesktopWeb) {
            if (isOpen) {
                setIsExpanded(true);
                //For resize from sm to md/lg
                if (searchText.length === 0) {
                    setIsOpen(false);
                    setReason(null);
                }
            }
            else {
                if (reason !== 'user-initiated') {
                    setSearchText('');
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                    if (reason === 'blur-closed') {
                        setReason(null);
                    }
                }
                if (reason)
                    leftBtnRef.current?.focus();
            }
        }
    }, [isDesktopWeb, isOpen, disableAnimation, reason]);
    react_1.default.useEffect(() => {
        if (disableAnimation)
            return;
        react_native_1.Animated.timing(animProgress, {
            toValue: isExpanded ? 1 : 0,
            duration: isExpanded ? 300 : 250,
            useNativeDriver: false,
        }).start(() => {
            if (isExpanded) {
                inputRef.current?.focus();
            }
        });
    }, [isExpanded, disableAnimation]);
    const handleMagnifyingGlassClick = () => {
        if (closeSidebar)
            react_native_1.DeviceEventEmitter.emit('event.hideCompanyMenu');
        if (disableAnimation) {
            if (searchText.trim() && onSearch)
                onSearch(searchText);
            return;
        }
        if (!isDesktopWeb) {
            if (!isOpen) {
                setIsOpen(true);
                setReason(null);
            }
            else {
                if (searchText.trim() && onSearch) {
                    onSearch(searchText);
                }
            }
            return;
        }
        if (!isExpanded) {
            setIsExpanded(true);
        }
        else if (searchText.trim() && onSearch) {
            onSearch(searchText);
        }
    };
    const handleClose = () => {
        setIsOpen(false);
        setReason(null);
        if (onSearch)
            onSearch('');
    };
    // Interpolations
    const capsuleWidth = disableAnimation ? maxWidth : animProgress.interpolate({
        inputRange: [0, 1],
        outputRange: [36, maxWidth],
    });
    const contentOpacity = disableAnimation ? 1 : animProgress.interpolate({
        inputRange: [0, 0.4, 1],
        outputRange: [0, 0, 1],
    });
    const { ref: floatingRef, onKeyDown, ...cleanRestProps } = restProps;
    const mergedInputRef = (0, react_2.useMergeRefs)([inputRef, ref, floatingRef]);
    return ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { ...styles.headerContent, ...(disableAnimation && { width: maxWidth }) }, children: !isDesktopWeb && !disableAnimation ? ((0, jsx_runtime_1.jsx)(react_native_1.Pressable, { hitSlop: 4, style: ({ pressed }) => [
                styles.iconButtonSM,
                pressed ? { backgroundColor: '#0000003c' } : null
            ], onPress: isOpen ? handleClose : handleMagnifyingGlassClick, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: isOpen ? Theme_1.ICONS.Regular.Xmark : Theme_1.ICONS.Solid.MagnifyingGlass, size: 16, color: isOpen ? THEME.Black : THEME.AlwaysWhite }) })) : ((0, jsx_runtime_1.jsxs)(react_native_1.Animated.View, { ref: capsuleRef, style: [styles.searchCapsule, { width: capsuleWidth }], children: [(0, jsx_runtime_1.jsx)(react_native_1.TouchableOpacity, { role: "button", ref: leftBtnRef, "aria-label": finalPlaceholder, activeOpacity: 0.7, onPress: handleMagnifyingGlassClick, style: [styles.iconTouchArea, styles.leftOverlay], disabled: disableAnimation || isExpanded, "aria-hidden": isExpanded, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Solid.MagnifyingGlass, size: 15, color: THEME.AlwaysWhite }) }), (0, jsx_runtime_1.jsx)(react_native_1.Animated.View, { pointerEvents: isExpanded ? 'auto' : 'none', style: [styles.inputWrapper, { opacity: contentOpacity }], "aria-hidden": !isExpanded, children: (0, jsx_runtime_1.jsx)(react_native_paper_1.TextInput, { value: searchText, placeholder: finalPlaceholder, placeholderTextColor: THEME.AlwaysWhite, cursorColor: THEME.AlwaysWhite, textColor: THEME.AlwaysWhite, mode: "flat", underlineColor: "transparent", activeUnderlineColor: "transparent", style: [
                            styles.paperInput,
                            {
                                backgroundColor: 'transparent',
                                margin: 0,
                                paddingHorizontal: 0,
                            }
                        ], contentStyle: {
                            paddingLeft: ICON_HEIGHT + 2,
                            paddingRight: ICON_HEIGHT + 2,
                            height: CAPSULE_HEIGHT,
                            lineHeight: CAPSULE_HEIGHT,
                            paddingVertical: 0,
                        }, dense: true, editable: disableAnimation || isExpanded, tabIndex: (disableAnimation || isExpanded) ? 0 : -1, "aria-hidden": !isExpanded, render: (props) => {
                            return ((0, jsx_runtime_1.jsx)(react_native_1.TextInput, { ...props, ...cleanRestProps, ref: mergedInputRef, onKeyPress: onKeyDown }));
                        } }) }), (0, jsx_runtime_1.jsx)(react_native_1.TouchableOpacity, { ref: rightBtnRef, onPress: handleClose, style: [
                        styles.rightOverlay,
                        styles.iconTouchArea,
                        { opacity: isExpanded ? 1 : 0 }
                    ], 
                    //@ts-ignore
                    tabIndex: -1, children: (0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles.closeCircleButton, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Regular.Xmark, size: 12, color: THEME.Black }) }) })] })) }));
});
exports.default = ExpandableSearchBar;
